import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { db, auth, storage } from '../firebase.config';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';

const AppContext = createContext();

// Default categories to initialize for new users
const DEFAULT_CATEGORIES = [
  { id: 'cat-family', name: 'Family', color: '#dc143c', description: 'Family members' },
  { id: 'cat-friends', name: 'Friends', color: '#22c55e', description: 'Personal friends' },
  { id: 'cat-professional', name: 'Professional', color: '#1e3a5f', description: 'Work and business contacts' },
  { id: 'cat-acquaintance', name: 'Acquaintance', color: '#6c757d', description: 'Casual acquaintances' }
];

export function AppProvider({ children }) {
  const [contacts, setContacts] = useState([]);
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const tagsRef = useRef([]);

  // Keep tagsRef in sync with tags state
  useEffect(() => {
    tagsRef.current = tags;
  }, [tags]);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Get user-specific collection path
  const getUserCollection = useCallback((collectionName) => {
    if (!user) return null;
    return collection(db, 'users', user.uid, collectionName);
  }, [user]);

  // Fetch all contacts
  const fetchContacts = useCallback(async (filters = {}) => {
    if (!user) return [];
    setLoading(true);
    try {
      const contactsRef = getUserCollection('contacts');
      let q = query(contactsRef, orderBy('created_at', 'desc'));

      if (filters.category) {
        q = query(contactsRef, where('category', '==', filters.category), orderBy('created_at', 'desc'));
      }

      const snapshot = await getDocs(q);
      let contactsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Client-side filtering for search and tag (Firestore doesn't support contains queries easily)
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        // Resolve tag IDs to names for searching
        const tagNameMap = {};
        tagsRef.current.forEach(t => { tagNameMap[t.id] = t.name?.toLowerCase() || ''; });

        contactsList = contactsList.filter(c => {
          // Name
          if (c.first_name?.toLowerCase().includes(searchLower)) return true;
          if (c.last_name?.toLowerCase().includes(searchLower)) return true;
          const fullName = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
          if (fullName.includes(searchLower)) return true;

          // Email - legacy string field and emails array
          if (c.email?.toLowerCase().includes(searchLower)) return true;
          if (Array.isArray(c.emails) && c.emails.some(e =>
            (typeof e === 'string' ? e : e?.value)?.toLowerCase()?.includes(searchLower)
          )) return true;

          // Company & Job Title
          if (c.company?.toLowerCase().includes(searchLower)) return true;
          if (c.job_title?.toLowerCase().includes(searchLower)) return true;

          // Tags - match against tag names
          if (Array.isArray(c.tags) && c.tags.some(tagId =>
            tagNameMap[tagId]?.includes(searchLower)
          )) return true;

          // Notes, interests, context fields
          if (c.notes?.toLowerCase().includes(searchLower)) return true;
          if (c.interests?.toLowerCase().includes(searchLower)) return true;
          if (c.how_we_met?.toLowerCase().includes(searchLower)) return true;
          if (c.relationship_context?.toLowerCase().includes(searchLower)) return true;

          // Address, category, social
          if (c.address?.toLowerCase().includes(searchLower)) return true;
          if (c.category?.toLowerCase().includes(searchLower)) return true;
          if (c.twitter_handle?.toLowerCase().includes(searchLower)) return true;

          return false;
        });
      }

      if (filters.tag) {
        contactsList = contactsList.filter(c =>
          c.tags && c.tags.includes(filters.tag)
        );
      }

      setContacts(contactsList);
      return contactsList;
    } catch (error) {
      console.error('Error fetching contacts:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [user, getUserCollection]);

  // Fetch single contact
  const fetchContact = useCallback(async (id) => {
    if (!user) return null;
    try {
      const contactRef = doc(db, 'users', user.uid, 'contacts', id);
      const snapshot = await getDoc(contactRef);
      if (!snapshot.exists()) return null;
      return { id: snapshot.id, ...snapshot.data() };
    } catch (error) {
      console.error('Error fetching contact:', error);
      return null;
    }
  }, [user]);

  // Check for duplicate contacts
  const checkDuplicateContact = useCallback(async (data) => {
    if (!user) return null;

    const contactsRef = getUserCollection('contacts');
    const snapshot = await getDocs(contactsRef);
    const existingContacts = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

    // Helper to get all emails from a contact (supports both string and array)
    const getEmails = (contact) => {
      if (!contact.email && !contact.emails) return [];
      if (Array.isArray(contact.emails)) return contact.emails.map(e => e.value?.toLowerCase() || e.toLowerCase());
      if (contact.email) return [contact.email.toLowerCase()];
      return [];
    };

    const newEmails = getEmails(data);
    const newName = `${data.first_name} ${data.last_name || ''}`.toLowerCase().trim();

    for (const existing of existingContacts) {
      const existingEmails = getEmails(existing);
      const existingName = `${existing.first_name} ${existing.last_name || ''}`.toLowerCase().trim();

      // Check email match
      const emailMatch = newEmails.some(e => existingEmails.includes(e));
      if (emailMatch) {
        return { type: 'email', existingContact: existing };
      }

      // Check name match
      if (newName && newName === existingName) {
        return { type: 'name', existingContact: existing };
      }
    }

    return null;
  }, [user, getUserCollection]);

  // Create contact
  const createContact = useCallback(async (data, skipDuplicateCheck = false) => {
    if (!user) throw new Error('Not authenticated');
    try {
      const contactsRef = getUserCollection('contacts');
      const contactData = {
        ...data,
        category: data.category || 'acquaintance',
        relationship_strength: data.relationship_strength || 50,
        contact_frequency_goal: data.contact_frequency_goal || 30,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      };
      const docRef = await addDoc(contactsRef, contactData);
      const newContact = { id: docRef.id, ...contactData };
      setContacts(prev => [newContact, ...prev]);
      return newContact;
    } catch (error) {
      console.error('Error creating contact:', error);
      throw error;
    }
  }, [user, getUserCollection]);

  // Update contact
  const updateContact = useCallback(async (id, data) => {
    if (!user) throw new Error('Not authenticated');
    try {
      const contactRef = doc(db, 'users', user.uid, 'contacts', id);
      const updateData = {
        ...data,
        updated_at: serverTimestamp()
      };
      await updateDoc(contactRef, updateData);
      const updatedContact = { id, ...updateData };
      setContacts(prev => prev.map(c => c.id === id ? { ...c, ...updateData } : c));
      return updatedContact;
    } catch (error) {
      console.error('Error updating contact:', error);
      throw error;
    }
  }, [user]);

  // Delete contact
  const deleteContact = useCallback(async (id) => {
    if (!user) throw new Error('Not authenticated');
    try {
      const contactRef = doc(db, 'users', user.uid, 'contacts', id);
      await deleteDoc(contactRef);
      setContacts(prev => prev.filter(c => c.id !== id));
    } catch (error) {
      console.error('Error deleting contact:', error);
      throw error;
    }
  }, [user]);

  // Fetch tags
  const fetchTags = useCallback(async () => {
    if (!user) return [];
    try {
      const tagsRef = getUserCollection('tags');
      const snapshot = await getDocs(tagsRef);
      const tagsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTags(tagsList);
      return tagsList;
    } catch (error) {
      console.error('Error fetching tags:', error);
      return [];
    }
  }, [user, getUserCollection]);

  // Create tag
  const createTag = useCallback(async (data) => {
    if (!user) throw new Error('Not authenticated');
    try {
      const tagsRef = getUserCollection('tags');
      const tagData = {
        ...data,
        created_at: serverTimestamp()
      };
      const docRef = await addDoc(tagsRef, tagData);
      const newTag = { id: docRef.id, ...tagData };
      setTags(prev => [...prev, newTag]);
      return newTag;
    } catch (error) {
      console.error('Error creating tag:', error);
      throw error;
    }
  }, [user, getUserCollection]);

  // Update tag
  const updateTag = useCallback(async (id, data) => {
    if (!user) throw new Error('Not authenticated');
    try {
      const tagRef = doc(db, 'users', user.uid, 'tags', id);
      await updateDoc(tagRef, data);
      setTags(prev => prev.map(t => t.id === id ? { ...t, ...data } : t));
      return { id, ...data };
    } catch (error) {
      console.error('Error updating tag:', error);
      throw error;
    }
  }, [user]);

  // Delete tag
  const deleteTag = useCallback(async (id) => {
    if (!user) throw new Error('Not authenticated');
    try {
      const tagRef = doc(db, 'users', user.uid, 'tags', id);
      await deleteDoc(tagRef);
      setTags(prev => prev.filter(t => t.id !== id));
    } catch (error) {
      console.error('Error deleting tag:', error);
      throw error;
    }
  }, [user]);

  // Fetch categories (using default categories for now)
  const fetchCategories = useCallback(async () => {
    setCategories(DEFAULT_CATEGORIES);
    return DEFAULT_CATEGORIES;
  }, []);

  // Fetch dashboard stats
  const fetchStats = useCallback(async () => {
    if (!user) return null;
    try {
      const contactsRef = getUserCollection('contacts');
      const interactionsRef = getUserCollection('interactions');

      const [contactsSnap, interactionsSnap] = await Promise.all([
        getDocs(contactsRef),
        getDocs(interactionsRef)
      ]);

      const contactsList = contactsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const interactionsList = interactionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Calculate category counts
      const categoryCounts = contactsList.reduce((acc, c) => {
        const cat = c.category || 'acquaintance';
        acc[cat] = (acc[cat] || 0) + 1;
        return acc;
      }, {});

      // Get recent interactions
      const recentInteractions = interactionsList
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 5)
        .map(i => {
          const contact = contactsList.find(c => c.id === i.contact_id);
          return {
            ...i,
            first_name: contact?.first_name,
            last_name: contact?.last_name
          };
        });

      // Calculate stale contacts
      const now = new Date();
      const staleContacts = contactsList
        .map(c => {
          const lastContact = c.last_contacted ? new Date(c.last_contacted) : new Date(c.created_at?.seconds * 1000 || now);
          const daysSince = Math.floor((now - lastContact) / (1000 * 60 * 60 * 24));
          return { ...c, days_since_contact: daysSince };
        })
        .filter(c => c.days_since_contact > (c.contact_frequency_goal || 30))
        .sort((a, b) => b.days_since_contact - a.days_since_contact)
        .slice(0, 5);

      // Interactions by type
      const interactionsByType = interactionsList.reduce((acc, i) => {
        acc[i.type] = (acc[i.type] || 0) + 1;
        return acc;
      }, {});

      return {
        totalContacts: contactsList.length,
        totalInteractions: interactionsList.length,
        categoryCounts: Object.entries(categoryCounts).map(([category, count]) => ({ category, count })),
        recentInteractions,
        staleContacts,
        interactionsByType: Object.entries(interactionsByType).map(([type, count]) => ({ type, count }))
      };
    } catch (error) {
      console.error('Error fetching stats:', error);
      return null;
    }
  }, [user, getUserCollection]);

  // Fetch interactions for a contact
  const fetchInteractions = useCallback(async (contactId) => {
    if (!user) return [];
    try {
      const interactionsRef = getUserCollection('interactions');
      const q = query(interactionsRef, where('contact_id', '==', contactId), orderBy('date', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching interactions:', error);
      return [];
    }
  }, [user, getUserCollection]);

  // Create interaction
  const createInteraction = useCallback(async (data) => {
    if (!user) throw new Error('Not authenticated');
    try {
      const interactionsRef = getUserCollection('interactions');
      const interactionData = {
        ...data,
        created_at: serverTimestamp()
      };
      const docRef = await addDoc(interactionsRef, interactionData);

      // Update last_contacted on the contact
      if (data.contact_id) {
        const contactRef = doc(db, 'users', user.uid, 'contacts', data.contact_id);
        await updateDoc(contactRef, {
          last_contacted: data.date,
          updated_at: serverTimestamp()
        });
      }

      return { id: docRef.id, ...interactionData };
    } catch (error) {
      console.error('Error creating interaction:', error);
      throw error;
    }
  }, [user, getUserCollection]);

  // Delete interaction
  const deleteInteraction = useCallback(async (id) => {
    if (!user) throw new Error('Not authenticated');
    try {
      const interactionRef = doc(db, 'users', user.uid, 'interactions', id);
      await deleteDoc(interactionRef);
    } catch (error) {
      console.error('Error deleting interaction:', error);
      throw error;
    }
  }, [user]);

  // Fetch connections for a contact
  const fetchConnections = useCallback(async (contactId) => {
    if (!user) return [];
    try {
      const connectionsRef = getUserCollection('connections');
      const q1 = query(connectionsRef, where('contact_id_1', '==', contactId));
      const q2 = query(connectionsRef, where('contact_id_2', '==', contactId));

      const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);

      const connections = [
        ...snap1.docs.map(doc => ({ id: doc.id, ...doc.data() })),
        ...snap2.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      ];

      // Fetch connected contact names
      const contactIds = connections.flatMap(c => [c.contact_id_1, c.contact_id_2]).filter(id => id !== contactId);
      const uniqueIds = [...new Set(contactIds)];

      const contactPromises = uniqueIds.map(id =>
        getDoc(doc(db, 'users', user.uid, 'contacts', id))
      );
      const contactDocs = await Promise.all(contactPromises);
      const contactsMap = {};
      contactDocs.forEach(d => {
        if (d.exists()) contactsMap[d.id] = d.data();
      });

      return connections.map(c => {
        const otherId = c.contact_id_1 === contactId ? c.contact_id_2 : c.contact_id_1;
        const other = contactsMap[otherId] || {};
        return {
          ...c,
          connected_contact: {
            id: otherId,
            first_name: other.first_name,
            last_name: other.last_name
          }
        };
      });
    } catch (error) {
      console.error('Error fetching connections:', error);
      return [];
    }
  }, [user, getUserCollection]);

  // Create connection
  const createConnection = useCallback(async (data) => {
    if (!user) throw new Error('Not authenticated');
    try {
      // Ensure consistent ordering to prevent duplicates
      const [id1, id2] = [data.contact_id_1, data.contact_id_2].sort();

      const connectionsRef = getUserCollection('connections');
      const connectionData = {
        contact_id_1: id1,
        contact_id_2: id2,
        relationship_type: data.relationship_type || null,
        notes: data.notes || null,
        created_at: serverTimestamp()
      };
      const docRef = await addDoc(connectionsRef, connectionData);
      return { id: docRef.id, ...connectionData };
    } catch (error) {
      console.error('Error creating connection:', error);
      throw error;
    }
  }, [user, getUserCollection]);

  // Delete connection
  const deleteConnection = useCallback(async (id) => {
    if (!user) throw new Error('Not authenticated');
    try {
      const connectionRef = doc(db, 'users', user.uid, 'connections', id);
      await deleteDoc(connectionRef);
    } catch (error) {
      console.error('Error deleting connection:', error);
      throw error;
    }
  }, [user]);

  // Fetch network graph data
  const fetchNetworkGraph = useCallback(async () => {
    if (!user) return { contacts: [], connections: [] };
    try {
      const [contactsSnap, connectionsSnap] = await Promise.all([
        getDocs(getUserCollection('contacts')),
        getDocs(getUserCollection('connections'))
      ]);

      return {
        contacts: contactsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })),
        connections: connectionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      };
    } catch (error) {
      console.error('Error fetching network:', error);
      return { contacts: [], connections: [] };
    }
  }, [user, getUserCollection]);

  // CSV field mapping
  const CSV_FIELD_MAP = {
    'first name': 'first_name',
    'firstname': 'first_name',
    'first': 'first_name',
    'last name': 'last_name',
    'lastname': 'last_name',
    'last': 'last_name',
    'email': 'email',
    'email address': 'email',
    'phone': 'phone',
    'company': 'company',
    'job title': 'job_title',
    'title': 'job_title',
    'position': 'job_title',
    'linkedin': 'linkedin_url',
    'linkedin url': 'linkedin_url',
    'url': 'linkedin_url',
    'linkedin connected': 'linkedin_connected_date',
    'linkedin connected date': 'linkedin_connected_date',
    'connected on': 'linkedin_connected_date',
    'twitter': 'twitter_handle',
    'twitter handle': 'twitter_handle',
    'address': 'address',
    'birthday': 'birthday',
    'how we met': 'how_we_met',
    'notes': 'notes',
    'interests': 'interests',
    'category': 'category'
  };

  // Parse CSV and detect duplicates
  const parseCSVForDuplicates = async (file) => {
    if (!user) throw new Error('Not authenticated');

    const text = await file.text();
    const lines = text.split('\n').filter(line => line.trim());

    if (lines.length < 2) {
      throw new Error('CSV must have headers and at least one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));

    // Fetch existing contacts
    const contactsRef = getUserCollection('contacts');
    const snapshot = await getDocs(contactsRef);
    const existingContacts = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

    const newContacts = [];
    const duplicates = [];
    const parseErrors = [];

    for (let i = 1; i < lines.length; i++) {
      try {
        const values = parseCSVLine(lines[i]);
        const data = {};

        headers.forEach((header, index) => {
          const value = values[index]?.trim() || '';
          const fieldName = CSV_FIELD_MAP[header] || header.replace(/\s+/g, '_');
          if (value) data[fieldName] = value;
        });

        if (!data.first_name) continue;

        // Check for duplicates by email (primary) or name match
        const matchingContact = existingContacts.find(existing => {
          // Email match (most reliable)
          if (data.email && existing.email &&
              data.email.toLowerCase() === existing.email.toLowerCase()) {
            return true;
          }
          // Full name match
          const newName = `${data.first_name} ${data.last_name || ''}`.toLowerCase().trim();
          const existingName = `${existing.first_name} ${existing.last_name || ''}`.toLowerCase().trim();
          return newName === existingName;
        });

        if (matchingContact) {
          duplicates.push({
            csvLine: i + 1,
            csvData: data,
            existingContact: matchingContact,
            matchType: data.email && matchingContact.email &&
                       data.email.toLowerCase() === matchingContact.email.toLowerCase()
                       ? 'email' : 'name'
          });
        } else {
          newContacts.push({ csvLine: i + 1, data });
        }
      } catch (err) {
        parseErrors.push({ line: i + 1, error: err.message });
      }
    }

    return {
      newContacts,
      duplicates,
      parseErrors,
      totalRows: lines.length - 1
    };
  };

  // Import contacts from CSV with duplicate handling
  // duplicateAction: 'skip' | 'merge' | 'create' | { [csvLine]: 'skip' | 'merge' | 'create' }
  const importCSV = async (file, duplicateAction = 'skip') => {
    if (!user) throw new Error('Not authenticated');
    try {
      const { newContacts, duplicates, parseErrors } = await parseCSVForDuplicates(file);

      const batch = writeBatch(db);
      const contactsRef = getUserCollection('contacts');
      const imported = [];
      const merged = [];
      const skipped = [];

      // Import new contacts
      for (const { data } of newContacts) {
        const newDocRef = doc(contactsRef);
        batch.set(newDocRef, {
          ...data,
          category: data.category || 'acquaintance',
          relationship_strength: 50,
          contact_frequency_goal: 30,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp()
        });
        imported.push({ id: newDocRef.id, ...data });
      }

      // Handle duplicates based on action
      for (const dup of duplicates) {
        const action = typeof duplicateAction === 'object'
          ? duplicateAction[dup.csvLine] || 'skip'
          : duplicateAction;

        if (action === 'skip') {
          skipped.push(dup);
        } else if (action === 'merge') {
          // Merge: update existing contact with new data (only fill empty fields)
          const existingRef = doc(db, 'users', user.uid, 'contacts', dup.existingContact.id);
          const mergeData = { updated_at: serverTimestamp() };

          // Only add fields that are empty in existing but present in CSV
          Object.keys(dup.csvData).forEach(key => {
            if (dup.csvData[key] && !dup.existingContact[key]) {
              mergeData[key] = dup.csvData[key];
            }
          });

          batch.update(existingRef, mergeData);
          merged.push(dup);
        } else if (action === 'create') {
          // Create as new contact anyway
          const newDocRef = doc(contactsRef);
          batch.set(newDocRef, {
            ...dup.csvData,
            category: dup.csvData.category || 'acquaintance',
            relationship_strength: 50,
            contact_frequency_goal: 30,
            created_at: serverTimestamp(),
            updated_at: serverTimestamp()
          });
          imported.push({ id: newDocRef.id, ...dup.csvData });
        }
      }

      await batch.commit();
      await fetchContacts();

      return {
        imported: imported.length,
        merged: merged.length,
        skipped: skipped.length,
        errors: parseErrors.length,
        errorDetails: parseErrors
      };
    } catch (error) {
      console.error('Error importing CSV:', error);
      throw error;
    }
  };

  // Upload document
  const uploadDocument = useCallback(async (file, contactId, interactionId, description) => {
    if (!user) throw new Error('Not authenticated');
    try {
      const filename = `${Date.now()}_${file.name}`;
      const storageRef = ref(storage, `users/${user.uid}/documents/${filename}`);

      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);

      const documentsRef = getUserCollection('documents');
      const docData = {
        filename,
        original_name: file.name,
        mime_type: file.type,
        size: file.size,
        storage_path: `users/${user.uid}/documents/${filename}`,
        download_url: downloadUrl,
        contact_id: contactId || null,
        interaction_id: interactionId || null,
        description: description || null,
        created_at: serverTimestamp()
      };

      const docRef = await addDoc(documentsRef, docData);
      return { id: docRef.id, ...docData };
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  }, [user, getUserCollection]);

  // Fetch all events
  const fetchEvents = useCallback(async (filters = {}) => {
    if (!user) return [];
    setLoading(true);
    try {
      const eventsRef = getUserCollection('events');
      const q = query(eventsRef, orderBy('date', 'desc'));
      const snapshot = await getDocs(q);

      let eventsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        eventsList = eventsList.filter(e =>
          e.title?.toLowerCase().includes(searchLower) ||
          e.description?.toLowerCase().includes(searchLower) ||
          e.location?.toLowerCase().includes(searchLower)
        );
      }

      if (filters.event_type) {
        eventsList = eventsList.filter(e => e.event_type === filters.event_type);
      }

      if (filters.timeframe === 'upcoming') {
        const today = new Date().toISOString().split('T')[0];
        eventsList = eventsList.filter(e => e.date >= today);
        eventsList.sort((a, b) => a.date.localeCompare(b.date));
      } else if (filters.timeframe === 'past') {
        const today = new Date().toISOString().split('T')[0];
        eventsList = eventsList.filter(e => e.date < today);
      }

      return eventsList;
    } catch (error) {
      console.error('Error fetching events:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [user, getUserCollection]);

  // Fetch single event
  const fetchEvent = useCallback(async (id) => {
    if (!user) return null;
    try {
      const eventRef = doc(db, 'users', user.uid, 'events', id);
      const snapshot = await getDoc(eventRef);
      if (!snapshot.exists()) return null;
      return { id: snapshot.id, ...snapshot.data() };
    } catch (error) {
      console.error('Error fetching event:', error);
      return null;
    }
  }, [user]);

  // Create event with auto-generated interactions for attendees
  const createEvent = useCallback(async (data) => {
    if (!user) throw new Error('Not authenticated');
    try {
      const batch = writeBatch(db);

      const eventsRef = getUserCollection('events');
      const eventRef = doc(eventsRef);
      const eventData = {
        title: data.title,
        event_type: data.event_type || 'other',
        description: data.description || null,
        date: data.date,
        location: data.location || null,
        attendee_ids: data.attendee_ids || [],
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      };
      batch.set(eventRef, eventData);

      const attendeeIds = data.attendee_ids || [];
      const interactionsRef = getUserCollection('interactions');
      const today = new Date().toISOString().split('T')[0];

      for (const contactId of attendeeIds) {
        const contactRef = doc(db, 'users', user.uid, 'contacts', contactId);
        const contactSnap = await getDoc(contactRef);
        if (!contactSnap.exists()) continue;

        const interactionRef = doc(interactionsRef);
        batch.set(interactionRef, {
          contact_id: contactId,
          type: 'event',
          date: data.date,
          subject: `Attended: ${data.title}`,
          notes: data.description || null,
          location: data.location || null,
          event_id: eventRef.id,
          created_at: serverTimestamp()
        });

        if (data.date <= today) {
          batch.update(contactRef, {
            last_contacted: data.date,
            updated_at: serverTimestamp()
          });
        }
      }

      await batch.commit();
      return { id: eventRef.id, ...eventData };
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  }, [user, getUserCollection]);

  // Update event with attendee diff sync
  const updateEvent = useCallback(async (id, data) => {
    if (!user) throw new Error('Not authenticated');
    try {
      const eventRef = doc(db, 'users', user.uid, 'events', id);
      const eventSnap = await getDoc(eventRef);
      if (!eventSnap.exists()) throw new Error('Event not found');

      const currentEvent = eventSnap.data();
      const oldAttendeeIds = currentEvent.attendee_ids || [];
      const newAttendeeIds = data.attendee_ids !== undefined ? data.attendee_ids : oldAttendeeIds;

      const removedIds = oldAttendeeIds.filter(aid => !newAttendeeIds.includes(aid));
      const addedIds = newAttendeeIds.filter(aid => !oldAttendeeIds.includes(aid));

      const batch = writeBatch(db);

      batch.update(eventRef, {
        ...data,
        updated_at: serverTimestamp()
      });

      // Delete interactions for removed attendees
      if (removedIds.length > 0) {
        const interactionsRef = getUserCollection('interactions');
        const q = query(interactionsRef, where('event_id', '==', id));
        const snap = await getDocs(q);

        snap.docs.forEach(d => {
          const interaction = d.data();
          if (removedIds.includes(interaction.contact_id)) {
            batch.delete(d.ref);
          }
        });
      }

      // Create interactions for added attendees
      const eventDate = data.date || currentEvent.date;
      const eventTitle = data.title || currentEvent.title;
      const eventDescription = data.description !== undefined ? data.description : currentEvent.description;
      const eventLocation = data.location !== undefined ? data.location : currentEvent.location;
      const today = new Date().toISOString().split('T')[0];

      const interactionsRef = getUserCollection('interactions');
      for (const contactId of addedIds) {
        const contactRef = doc(db, 'users', user.uid, 'contacts', contactId);
        const contactSnap = await getDoc(contactRef);
        if (!contactSnap.exists()) continue;

        const interactionRef = doc(interactionsRef);
        batch.set(interactionRef, {
          contact_id: contactId,
          type: 'event',
          date: eventDate,
          subject: `Attended: ${eventTitle}`,
          notes: eventDescription || null,
          location: eventLocation || null,
          event_id: id,
          created_at: serverTimestamp()
        });

        if (eventDate <= today) {
          batch.update(contactRef, {
            last_contacted: eventDate,
            updated_at: serverTimestamp()
          });
        }
      }

      await batch.commit();
      return { id, ...data };
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  }, [user, getUserCollection]);

  // Delete event and cleanup auto-generated interactions
  const deleteEvent = useCallback(async (id) => {
    if (!user) throw new Error('Not authenticated');
    try {
      const batch = writeBatch(db);

      const interactionsRef = getUserCollection('interactions');
      const q = query(interactionsRef, where('event_id', '==', id));
      const snap = await getDocs(q);
      snap.docs.forEach(d => batch.delete(d.ref));

      const eventRef = doc(db, 'users', user.uid, 'events', id);
      batch.delete(eventRef);

      await batch.commit();
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  }, [user, getUserCollection]);

  // Fetch documents for a contact
  const fetchDocuments = useCallback(async (contactId) => {
    if (!user) return [];
    try {
      const documentsRef = getUserCollection('documents');
      const q = query(documentsRef, where('contact_id', '==', contactId));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching documents:', error);
      return [];
    }
  }, [user, getUserCollection]);

  const value = React.useMemo(() => ({
    user,
    authLoading,
    contacts,
    tags,
    categories,
    loading,
    fetchContacts,
    fetchContact,
    checkDuplicateContact,
    createContact,
    updateContact,
    deleteContact,
    fetchTags,
    createTag,
    updateTag,
    deleteTag,
    fetchCategories,
    fetchStats,
    fetchInteractions,
    createInteraction,
    deleteInteraction,
    fetchConnections,
    createConnection,
    deleteConnection,
    fetchNetworkGraph,
    parseCSVForDuplicates,
    importCSV,
    uploadDocument,
    fetchDocuments,
    fetchEvents,
    fetchEvent,
    createEvent,
    updateEvent,
    deleteEvent
  }), [
    user,
    authLoading,
    contacts,
    tags,
    categories,
    loading,
    fetchContacts,
    fetchContact,
    checkDuplicateContact,
    createContact,
    updateContact,
    deleteContact,
    fetchTags,
    createTag,
    updateTag,
    deleteTag,
    fetchCategories,
    fetchStats,
    fetchInteractions,
    createInteraction,
    deleteInteraction,
    fetchConnections,
    createConnection,
    deleteConnection,
    fetchNetworkGraph,
    parseCSVForDuplicates,
    importCSV,
    uploadDocument,
    fetchDocuments,
    fetchEvents,
    fetchEvent,
    createEvent,
    updateEvent,
    deleteEvent
  ]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

// Helper to parse CSV lines with quoted values
function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.replace(/^"|"$/g, ''));
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.replace(/^"|"$/g, ''));
  return result;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
